'use client';

import Link from 'next/link';
import { useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { TbArrowUpRight } from 'react-icons/tb';

interface CtaButtonProps {
    href: string;
    children: ReactNode;
    /** Colors, padding, radius, hover-color states — everything except transform/shadow. */
    linkClassName: string;
    icon?: ReactNode | null;
    isAr?: boolean;
    className?: string;
    shadowClassName?: string;
    intensity?: number;
}

/**
 * Neubrutalist "follow" CTA: a solid shadow panel stays fixed behind the button
 * while the face does a magnetic, spring-driven translate toward the cursor —
 * the gap between face and shadow changes with pointer position instead of
 * being a static drop-shadow.
 */
export default function CtaButton({
    href,
    children,
    linkClassName,
    icon,
    isAr = false,
    className = '',
    shadowClassName = 'bg-[#081821]',
    intensity = 0.25,
}: CtaButtonProps) {
    const ref = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState({ x: 0, y: 0 });

    const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
        const { clientX, clientY } = e;
        const { height, width, left, top } = ref.current!.getBoundingClientRect();
        setPosition({
            x: (clientX - (left + width / 2)) * intensity,
            y: (clientY - (top + height / 2)) * intensity,
        });
    };

    const reset = () => setPosition({ x: 0, y: 0 });

    return (
        <div
            ref={ref}
            onMouseMove={handleMouseMove}
            onMouseLeave={reset}
            className={`relative inline-block self-start ${className}`}
        >
            <div aria-hidden="true" className={`absolute inset-0 translate-x-[7px] translate-y-[7px] ${shadowClassName}`} />
            <motion.div
                animate={{ x: position.x, y: position.y }}
                transition={{ type: 'spring', stiffness: 150, damping: 15, mass: 0.1 }}
                className="relative"
            >
                <Link href={href} className={`border-2 border-[#081821] ${linkClassName}`}>
                    {children}
                    {icon !== null && (icon ?? <TbArrowUpRight className={`h-5 w-5 ${isAr ? '-scale-x-100' : ''}`} aria-hidden="true" />)}
                </Link>
            </motion.div>
        </div>
    );
}
